import React from "react";
import QRCode from "react-qr-code";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { cdnAssetUrl } from "../../config/staticAssets";
import { DOWNLOAD_LINKS, FEATURE_CARDS, STORE_BADGES } from "./downloadAppContent";

function ServiceCard({ item }) {
  return (
    <article className="wlj-about-service-card">
      <div className="wlj-about-service-card-inner">
        <div className="wlj-about-service-card-icon">
          <img src={item.icon} alt="" />
        </div>
        <h3>{item.title}</h3>
        <p>{item.text}</p>
        <div className="wlj-about-service-card-shape" aria-hidden="true">
          <img src={cdnAssetUrl("assets/img/shape/srv_shape.png")} alt="" />
        </div>
        <span className="wlj-about-service-card-overlay" aria-hidden="true" />
      </div>
    </article>
  );
}

function DownloadQr({ value, label }) {
  return (
    <div className="wlj-download-qr-wrap" aria-label={label}>
      <QRCode
        value={value}
        size={88}
        bgColor="#ffffff"
        fgColor="#012660"
        className="wlj-download-qr"
      />
    </div>
  );
}

function DownloadAppBody() {
  const { t } = useTranslation();

  return (
    <>
      <div className="wlj-download-body">
        <section className="wlj-download-hero">
          <div className="wlj-container">
            <h2 className="wlj-download-hero-title">{t("Marketing.DownloadAppTitle")}</h2>
            <div className="wlj-download-hero-grid">
              <div className="wlj-download-stores">
                <div className="wlj-download-store-block">
                  <div className="wlj-download-divider">{t("Marketing.DownloadAndroid")}</div>
                  <div className="wlj-download-store-row">
                    <a
                      href={DOWNLOAD_LINKS.android}
                      className="wlj-download-store-link"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        className="wlj-download-store-badge wlj-download-store-badge--google"
                        src={STORE_BADGES.googlePlay}
                        alt={t("Marketing.GetGooglePlay")}
                      />
                    </a>
                    <DownloadQr value={DOWNLOAD_LINKS.android} label={t("Marketing.AndroidQr")} />
                  </div>
                </div>

                <div className="wlj-download-store-block">
                  <div className="wlj-download-divider">{t("Marketing.DownloadIos")}</div>
                  <div className="wlj-download-store-row">
                    <a
                      href={DOWNLOAD_LINKS.ios}
                      className="wlj-download-store-link"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        className="wlj-download-store-badge wlj-download-store-badge--apple"
                        src={STORE_BADGES.appStore}
                        alt={t("Marketing.GetAppStore")}
                      />
                    </a>
                    <DownloadQr value={DOWNLOAD_LINKS.ios} label={t("Marketing.IosQr")} />
                  </div>
                </div>
              </div>

              <div className="wlj-download-hero-media">
                <div className="wlj-download-hero-video-wrap">
                  <video autoPlay muted playsInline controls>
                    <source src={DOWNLOAD_LINKS.introVideo} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="wlj-download-mockup">
          <div className="wlj-container">
            <div className="wlj-download-mockup-grid">
              <div className="wlj-download-mockup-image">
                <img src={cdnAssetUrl("assets/img/about/mockup.png")} alt="Welinkjobs app mockup" />
              </div>
              <div className="wlj-download-mockup-copy">
                <h1 className="wlj-download-section-title">{t("Marketing.SearchApplyGlobally")}</h1>
                <p>
                  {t("Marketing.SearchApplyDesc")}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="wlj-about-fanfact" aria-label={t("Common.AppPreviewVideo")}>
        <div className="wlj-about-fanfact-media">
          <video
            className="wlj-about-fanfact-video"
            src={cdnAssetUrl("assets/img/about/newabout.mp4")}
            loop
            autoPlay
            muted
            playsInline
          />
        </div>
      </section>

      <section className="wlj-about-hotline" aria-label={t("Common.ContactHotline")}>
        <div className="wlj-container">
          <div className="wlj-about-hotline-inner">
            <div className="wlj-about-hotline-phone">
              <a href={DOWNLOAD_LINKS.hotlinePhone} className="wlj-about-hotline-icon" aria-label={t("Common.Phone")}>
                <FontAwesomeIcon icon={faPhone} />
              </a>
              <h3 className="wlj-about-hotline-number">{DOWNLOAD_LINKS.hotlineDisplay}</h3>
            </div>
            <div className="wlj-about-hotline-whatsapp">
              <p className="wlj-about-hotline-text">
                {t("Marketing.HotlineText")}
              </p>
              <a
                href={DOWNLOAD_LINKS.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="wlj-about-hotline-icon"
                aria-label={t("Common.WhatsApp")}
              >
                <FontAwesomeIcon icon={faWhatsapp} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="wlj-about-services wlj-download-services">
        <div className="wlj-container">
          <h1 className="wlj-download-features-title">{t("Marketing.FeaturesTitle")}</h1>
          <div className="wlj-about-services-grid">
            <div className="wlj-about-services-col">
              <div className="wlj-about-service-lead">
                <h4>{t("Marketing.JobSearchLeadTitle")}</h4>
                <p>
                  {t("Marketing.JobSearchLeadText")}
                </p>
              </div>
              <div className="wlj-about-service-photo">
                <img
                  src={cdnAssetUrl(
                    "assets/imagesforportal/complexities-hrm-through-compelling-graphic-featuring-magnifier-glass-zooming-man.jpg",
                  )}
                  alt="Job search and application"
                />
              </div>
              <ServiceCard item={FEATURE_CARDS[4]} />
            </div>

            <div className="wlj-about-services-col">
              <ServiceCard item={FEATURE_CARDS[0]} />
              <ServiceCard item={FEATURE_CARDS[1]} />
              <ServiceCard item={FEATURE_CARDS[5]} />
            </div>

            <div className="wlj-about-services-col">
              <ServiceCard item={FEATURE_CARDS[2]} />
              <ServiceCard item={FEATURE_CARDS[3]} />
            </div>
          </div>
        </div>
        <div className="wlj-about-services-shape wlj-about-services-shape--1" aria-hidden="true">
          <img src={cdnAssetUrl("assets/img/shape/srv_shape1.png")} alt="" />
        </div>
        <div className="wlj-about-services-shape wlj-about-services-shape--2" aria-hidden="true">
          <img src={cdnAssetUrl("assets/img/shape/srv_shape2.png")} alt="" />
        </div>
      </section>
    </>
  );
}

export default DownloadAppBody;
